import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CaretDownOutlined, CaretUpOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Alert, Modal, Spin, Table, message } from "antd";
import { applyGridSettings, loadGridSettings } from "@controls/grid-settings";
import { setSprZalGrafFactIsOk } from "@components/sprs/zal/spr-zal-api";
import ClientDrawer from "./client-drawer";
import ClientImportDrawer from "./client-import-drawer";
import { fetchClientImportStatus, fetchHealthClients, fetchHealthDoctors } from "./health-clients-api";
import { buildHealthClientColumns } from "./health-clients-columns";
import {
  DEFAULT_HIDDEN_COLUMN_KEYS,
  GRID_STORAGE_KEY,
  PERIOD_TYPE,
  defaultFilters
} from "./health-clients-constants";
import HealthClientsFilters from "./health-clients-filters";
import {
  filtersFromSearchParams,
  filtersSearchEquals,
  filtersToSearchParams,
  normalizeClientRow
} from "./health-clients-utils";
import "./health-clients.css";

const PAGE_SIZE = 100;
/** В режиме «По исполнителю» эти колонки показываем всегда. */
const EXECUTOR_FORCED_COLUMNS = ["visit", "tovName", "factIsOk", "zalName"];
const SCROLL_LOAD_GAP = 80;
const TABLE_HEADER_OFFSET = 48;
const SORT_FIELD_MAP = {
  status: "dateVipiska"
};
const DEFAULT_SORT = Object.freeze({ field: "name", order: "ascend" });
const SEARCH_DEBOUNCE_MS = 350;

const sortIcon = ({ sortOrder }) => {
  if (sortOrder === "ascend") return <CaretUpOutlined />;
  if (sortOrder === "descend") return <CaretDownOutlined />;
  return null;
};

const HealthClients = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { id: routeId } = useParams();
  const isNew = routeId === "new";
  const editId = routeId && /^\d+$/.test(routeId) ? Number(routeId) : null;
  const drawerOpen = isNew || editId != null;

  const userId = useSelector((state) => state.user.session?.userId);
  const [filters, setFilters] = useState(
    () => filtersFromSearchParams(window.location.search) || defaultFilters()
  );
  const [debouncedName, setDebouncedName] = useState(() => (filters.name || "").trim());
  const [doctors, setDoctors] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [tableScrollY, setTableScrollY] = useState(400);
  const [sort, setSort] = useState(DEFAULT_SORT);
  const [error, setError] = useState("");
  const [importAvailable, setImportAvailable] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [gridSettings, setGridSettings] = useState(
    () => loadGridSettings(GRID_STORAGE_KEY) || { order: [], hidden: DEFAULT_HIDDEN_COLUMN_KEYS }
  );
  const requestIdRef = useRef(0);
  const filtersRef = useRef(filters);
  const rowsRef = useRef([]);
  const loadingRef = useRef(false);
  const hasMoreRef = useRef(true);
  const sortRef = useRef(DEFAULT_SORT);
  const listBodyRef = useRef(null);
  const filterTimerRef = useRef(null);
  const skipUrlSyncRef = useRef(false);

  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  useEffect(() => {
    sortRef.current = sort;
  }, [sort]);

  useEffect(() => {
    if (userId == null) return;
    if (filters.periodType !== PERIOD_TYPE.EXECUTOR_DAY) return;
    setFilters((prev) => {
      if (prev.idSotr) return prev;
      return { ...prev, idSotr: userId };
    });
  }, [userId, filters.periodType]);

  useEffect(() => {
    const next = filtersToSearchParams(filters);
    const nextStr = next.toString();
    if (searchParams.toString() === nextStr) return undefined;
    const delay = (filters.name || "").trim() !== debouncedName ? SEARCH_DEBOUNCE_MS : 0;
    const t = setTimeout(() => {
      skipUrlSyncRef.current = true;
      setSearchParams(next, { replace: true });
    }, delay);
    return () => clearTimeout(t);
  }, [filters, debouncedName, searchParams, setSearchParams]);

  useEffect(() => {
    if (skipUrlSyncRef.current) {
      skipUrlSyncRef.current = false;
      return;
    }
    const fromUrl = filtersFromSearchParams(searchParams);
    if (!fromUrl) return;
    setFilters((prev) => (filtersSearchEquals(fromUrl, prev) ? prev : fromUrl));
    setDebouncedName((fromUrl.name || "").trim());
  }, [searchParams]);

  useEffect(() => {
    const t = setTimeout(
      () => setDebouncedName((filters.name || "").trim()),
      SEARCH_DEBOUNCE_MS
    );
    return () => clearTimeout(t);
  }, [filters.name]);

  useEffect(() => {
    let cancelled = false;
    fetchHealthDoctors()
      .then((data) => {
        if (!cancelled) setDoctors(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (!cancelled) setDoctors([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    fetchClientImportStatus()
      .then((data) => {
        if (!cancelled) setImportAvailable(Boolean(data?.enabled ?? data?.Enabled));
      })
      .catch(() => {
        if (!cancelled) setImportAvailable(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const loadItems = useCallback((nextFilters, options = {}) => {
    if (loadingRef.current && options.append) return;
    const applied = nextFilters ?? filtersRef.current;
    const append = Boolean(options.append);
    const appliedSort = options.sort ?? sortRef.current;
    const requestId = ++requestIdRef.current;
    loadingRef.current = true;
    setLoading(!append);
    setLoadingMore(append);
    setError("");

    fetchHealthClients({
      ...applied,
      skip: append ? rowsRef.current.length : 0,
      take: PAGE_SIZE,
      sortField: SORT_FIELD_MAP[appliedSort.field] || appliedSort.field,
      sortOrder: appliedSort.order === "descend" ? "desc" : "asc"
    })
      .then((data) => {
        if (requestId !== requestIdRef.current) return;
        const nextRows = (Array.isArray(data) ? data : []).map(normalizeClientRow);
        const merged = append ? [...rowsRef.current, ...nextRows] : nextRows;
        rowsRef.current = merged;
        setRows(merged);
        const more = nextRows.length === PAGE_SIZE;
        hasMoreRef.current = more;
        setHasMore(more);
      })
      .catch((e) => {
        if (requestId !== requestIdRef.current) return;
        setError(e.message || "Не удалось загрузить клиентов");
        if (!append) {
          rowsRef.current = [];
          setRows([]);
        }
      })
      .finally(() => {
        if (requestId === requestIdRef.current) {
          loadingRef.current = false;
          setLoading(false);
          setLoadingMore(false);
        }
      });
  }, []);

  useEffect(() => {
    filterTimerRef.current = window.setTimeout(() => {
      loadItems(filtersRef.current);
    }, 350);
    return () => {
      window.clearTimeout(filterTimerRef.current);
      filterTimerRef.current = null;
    };
  }, [
    filters.periodType,
    filters.dateBeg,
    filters.dateEnd,
    filters.onlyInComplex,
    filters.idSotr,
    filters.name,
    loadItems
  ]);

  const onFiltersChange = (next) => {
    setFilters(next);
  };

  const onPeriodTypeChange = useCallback(
    (periodType) => {
      const current = filtersRef.current;
      const next = {
        ...current,
        periodType,
        ...(periodType === PERIOD_TYPE.EXECUTOR_DAY
          ? {
              onlyInComplex: false,
              dateEnd: null,
              idSotr: current.idSotr || userId || 0
            }
          : {})
      };
      setFilters(next);
    },
    [userId]
  );

  const onRefresh = useCallback(() => {
    window.clearTimeout(filterTimerRef.current);
    filterTimerRef.current = null;
    loadItems(filtersRef.current);
  }, [loadItems]);

  const loadMore = useCallback(() => {
    if (loadingRef.current || !hasMoreRef.current) return;
    loadItems(filtersRef.current, { append: true, sort: sortRef.current });
  }, [loadItems]);

  useEffect(() => {
    const element = listBodyRef.current;
    if (!element || typeof ResizeObserver === "undefined") return undefined;
    const update = () => {
      setTableScrollY(Math.max(160, element.clientHeight - TABLE_HEADER_OFFSET));
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(element);
    return () => observer.disconnect();
  }, [loading, rows.length, error]);

  useEffect(() => {
    const tableBody = listBodyRef.current?.querySelector(".ant-table-body");
    if (!tableBody) return undefined;
    const onScroll = () => {
      if (
        tableBody.scrollTop + tableBody.clientHeight >=
        tableBody.scrollHeight - SCROLL_LOAD_GAP
      ) {
        loadMore();
      }
    };
    tableBody.addEventListener("scroll", onScroll);
    return () => tableBody.removeEventListener("scroll", onScroll);
  }, [loadMore, loading, rows.length, tableScrollY]);

  const openEdit = useCallback(
    (record) => {
      if (record?.idClient == null) return;
      navigate({ pathname: `/health/clients/${record.idClient}`, search: location.search });
    },
    [navigate, location.search]
  );

  const openCreate = useCallback(() => {
    navigate({ pathname: "/health/clients/new", search: location.search });
  }, [navigate, location.search]);

  const openImport = useCallback(() => {
    setImportOpen(true);
  }, []);

  const closeImport = useCallback(() => {
    setImportOpen(false);
  }, []);

  const onImported = useCallback(
    (imported) => {
      setImportOpen(false);
      loadItems(filtersRef.current);
      const count = Number(imported) || 0;
      message.success(`Загружено ${count} клиентов`);
    },
    [loadItems]
  );

  const closeEdit = useCallback(() => {
    navigate({ pathname: "/health/clients", search: location.search });
  }, [navigate, location.search]);

  const onClientSaved = useCallback(
    (created) => {
      loadItems(filtersRef.current);
      const newId = created?.idClient ?? created?.IdClient;
      if (newId != null) {
        navigate(
          { pathname: `/health/clients/${newId}`, search: location.search },
          { replace: true }
        );
        return;
      }
      closeEdit();
    },
    [closeEdit, loadItems, navigate, location.search]
  );

  const onClientDeleted = useCallback(() => {
    loadItems(filtersRef.current);
    closeEdit();
  }, [closeEdit, loadItems]);

  const onToggleFact = useCallback((row) => {
    if (!row?.idZal || !row?.zalIdStr || !row?.idClient) return;
    const nextIsOk = Number(row.factIsOk) === 1 ? 0 : 1;
    Modal.confirm({
      title: nextIsOk ? "Отметить, что клиент пришёл?" : "Снять отметку о приходе?",
      content: [row.name, row.tovName, row.zalName].filter(Boolean).join(" · "),
      okText: nextIsOk ? "Пришёл" : "Не пришёл",
      cancelText: "Отмена",
      onOk: () =>
        setSprZalGrafFactIsOk(row.idZal, {
          idStr: row.zalIdStr,
          idClient: row.idClient,
          idClientStrUsl: row.idClientStrUsl,
          isOk: nextIsOk
        })
          .then(() => {
            const update = (list) => list.map((r) => (r.key === row.key ? { ...r, factIsOk: nextIsOk } : r));
            rowsRef.current = update(rowsRef.current);
            setRows(update);
          })
          .catch((e) => {
            message.error(e.message || "Не удалось изменить отметку");
            return Promise.reject(e);
          })
    });
  }, []);

  const byExecutor = filters.periodType === PERIOD_TYPE.EXECUTOR_DAY;

  const allColumns = useMemo(
    () => buildHealthClientColumns(filters.periodType, { onToggleFact }),
    [filters.periodType, onToggleFact]
  );

  const dataColumns = useMemo(() => {
    if (!byExecutor) return applyGridSettings(allColumns, gridSettings);
    const applied = applyGridSettings(allColumns, {
      ...gridSettings,
      hidden: (gridSettings.hidden || []).filter((key) => !EXECUTOR_FORCED_COLUMNS.includes(key))
    });
    const forced = EXECUTOR_FORCED_COLUMNS.map((key) => applied.find((col) => col.key === key)).filter(Boolean);
    const status = applied.filter((col) => col.key === "status");
    const rest = applied.filter((col) => col.key !== "status" && !EXECUTOR_FORCED_COLUMNS.includes(col.key));
    return [...status, ...forced, ...rest];
  }, [allColumns, gridSettings, byExecutor]
  );

  const columns = useMemo(
    () =>
      dataColumns.map((column) => ({
        ...column,
        sorter: true,
        sortOrder: sort.field === column.key ? sort.order : null,
        sortDirections: ["ascend", "descend"],
        sortIcon,
        showSorterTooltip: false
      })),
    [dataColumns, sort]
  );

  const onTableChange = useCallback(
    (_, __, sorter) => {
      const current = Array.isArray(sorter) ? sorter[0] : sorter;
      const nextSort =
        current?.columnKey && current?.order
          ? { field: current.columnKey, order: current.order }
          : DEFAULT_SORT;
      if (nextSort.field === sortRef.current.field && nextSort.order === sortRef.current.order) return;
      sortRef.current = nextSort;
      setSort(nextSort);
      loadItems(filtersRef.current, { sort: nextSort });
    },
    [loadItems]
  );

  const gridColumnMetas = useMemo(
    () =>
      allColumns.map((col) => ({
        key: col.key,
        title: col.title
      })),
    [allColumns]
  );

  const onGridSettingsChange = useCallback((next) => {
    setGridSettings(next);
  }, []);

  const scrollX = useMemo(
    () => dataColumns.reduce((sum, col) => sum + (Number(col.width) || 120), 0),
    [dataColumns]
  );

  return (
    <div className="health-clients">
      <HealthClientsFilters
        value={filters}
        doctors={doctors}
        loading={loading}
        onChange={onFiltersChange}
        onRefresh={onRefresh}
        onCreate={openCreate}
        onImport={openImport}
        importAvailable={importAvailable}
        onPeriodTypeChange={onPeriodTypeChange}
        reportId={editId}
        gridColumns={gridColumnMetas}
        gridStorageKey={GRID_STORAGE_KEY}
        gridSettings={gridSettings}
        onGridSettingsChange={onGridSettingsChange}
      />
      {error ? <Alert className="health-clients__error" type="error" showIcon message={error} /> : null}
      <div className="health-clients__card">
        <div className="health-clients__body" ref={listBodyRef}>
          {loading && !rows.length ? (
            <div className="health-clients__center">
              <Spin tip="Загрузка клиентов..." />
            </div>
          ) : (
            <div className="health-clients__table-wrap">
              <Table
                className="health-clients__table"
                size="small"
                rowKey="key"
                columns={columns}
                dataSource={rows}
                pagination={false}
                scroll={{ x: scrollX, y: tableScrollY }}
                onChange={onTableChange}
                locale={{ emptyText: "Нет клиентов" }}
                onRow={(record) => ({
                  onClick: () => openEdit(record),
                  className:
                    editId != null && record?.idClient === editId
                      ? "health-clients__row health-clients__row--active"
                      : "health-clients__row"
                })}
              />
            </div>
          )}
        </div>
      </div>
      <ClientDrawer
        open={drawerOpen}
        idClient={editId}
        isNew={isNew}
        onClose={closeEdit}
        onSaved={onClientSaved}
        onDeleted={onClientDeleted}
      />
      <ClientImportDrawer open={importOpen} onClose={closeImport} onImported={onImported} />
    </div>
  );
};

export default HealthClients;
